import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { createAppTheme, THEME_STORAGE_KEY, THEME_CHOICES } from "./createAppTheme";

const ThemeSettingsContext = createContext(null);

export function useThemeSettings() {
    const ctx = useContext(ThemeSettingsContext);
    if (!ctx) {
        throw new Error("useThemeSettings must be used within AppThemeProvider");
    }
    return ctx;
}

export function AppThemeProvider({ children }) {
    const [themeId, setThemeIdState] = useState(() => {
        try {
            const stored = localStorage.getItem(THEME_STORAGE_KEY);
            if (stored && THEME_CHOICES.some((t) => t.id === stored)) return stored;
        } catch {
            /* ignore */
        }
        return "forest";
    });

    const setThemeId = useCallback((id) => {
        if (!THEME_CHOICES.some((t) => t.id === id)) return;
        setThemeIdState(id);
        try {
            localStorage.setItem(THEME_STORAGE_KEY, id);
        } catch {
            /* ignore */
        }
    }, []);

    const theme = useMemo(() => createAppTheme(themeId), [themeId]);

    const value = useMemo(
        () => ({
            themeId,
            setThemeId,
            choices: THEME_CHOICES,
        }),
        [themeId, setThemeId]
    );

    return (
        <ThemeSettingsContext.Provider value={value}>
            <ThemeProvider theme={theme}>
                <CssBaseline enableColorScheme />
                {children}
            </ThemeProvider>
        </ThemeSettingsContext.Provider>
    );
}
